import { HeroReport } from '../model/hero-report/hero-report.model';
import { sreCategoryPinColor } from './heroReportPinColor';

/** The two reports that can be exported from the events page. */
export type ExportReportType = 'SRE' | 'EOD';

/** The most reports one export can list; the API refuses more (hero-api#43). */
export const MAX_EXPORT_REPORTS = 100;

const ALL_STATUSES = ['DRAFT', 'PENDING', 'COMPLETED'];

/** Pin colors for reports that are not SRE, the same as the map on the events page. */
const STATUS_PIN_COLORS: Record<string, string> = {
  COMPLETED: '#41BE8A',
  PENDING: '#E75D4A',
};

/** Used for a status that has no color on the events page (a draft), so that its pin can still be seen on paper. */
const OTHER_STATUS_PIN_COLOR = '#9AA5A0';

/** The filters set above the events, as the events page keeps them. */
export interface EventFilterValues {
  organizationIds?: string[] | null;
  reportedByIds?: string[] | null;
  possibleIdentifications?: string[] | null;
  statuses?: string[] | null;
}

/** The body of the request that starts an export. */
export interface ExportRequest {
  statuses?: string[];
  reportedByIds?: string[];
  possibleIdentifications?: string[];
  mapDataUrl?: string;
}

/** A report on the map in the export. */
export interface ExportPin {
  /** The report's id as the PDF shows it, such as HR-1042. */
  label: string;
  longitude: number;
  latitude: number;
  color: string;
}

/** Why there is nothing to export, or why the export is not allowed. */
export type NotExportableReason = 'none' | 'multiple-organizations' | 'too-many';

/** `organizationId` is set when the reports can be exported, and `reason` when they cannot. */
export interface ExportCheck {
  ok: boolean;
  organizationId?: string;
  reason?: NotExportableReason;
}

/** The id a report has on the map and in the PDF. The API shows the same (`HR-1042`). */
export function reportNumberLabel(reportNumber: number | null | undefined): string {
  return `HR-${reportNumber ?? '?'}`;
}

/** SRE reports are the ones saved as SRE; every other report (UXO, or from before there were types) is an EOD report. */
export function isReportOfType(report: HeroReport, reportType: ExportReportType): boolean {
  const isSre = report.reportType === 'SRE';
  return reportType === 'SRE' ? isSre : !isSre;
}

/** The reports an export of this type lists, out of the reports on the events page. */
export function reportsToExport(events: HeroReport[], reportType: ExportReportType): HeroReport[] {
  return (events ?? []).filter((report) => isReportOfType(report, reportType));
}

/** The organizations the reports were made by, without repeats. */
export function organizationIdsOf(reports: HeroReport[]): string[] {
  const ids = new Set<string>();
  for (const report of reports) {
    const id = report.account?.organization?.id ?? report.account?.organizationId;
    if (id) {
      ids.add(id);
    }
  }
  return Array.from(ids);
}

/**
 * Decides whether these reports can be exported: there has to be at least one, no more than the API allows, and all
 * from one organization (an export is for one region and organization).
 */
export function checkExportable(reports: HeroReport[]): ExportCheck {
  if (reports.length === 0) {
    return { ok: false, reason: 'none' };
  }
  if (reports.length > MAX_EXPORT_REPORTS) {
    return { ok: false, reason: 'too-many' };
  }

  const organizationIds = organizationIdsOf(reports);
  if (organizationIds.length !== 1) {
    // None can only happen when the reports have no account; the API cannot export those either.
    return { ok: false, reason: organizationIds.length === 0 ? 'none' : 'multiple-organizations' };
  }

  return { ok: true, organizationId: organizationIds[0] };
}

/**
 * The statuses to send: the ones chosen in the Status filter (all of them if none was), without the ones the
 * pending/completed toggles hide. Returns nothing when all statuses are allowed.
 *
 * The result can be empty (for example Pending chosen and pending hidden); that means no report can match, so callers
 * check for reports before sending anything. An empty list is not sent, because the API reads it as "no filter".
 */
export function exportStatuses(
  selected: string[] | null | undefined,
  showPending: boolean,
  showCompleted: boolean,
): string[] | undefined {
  const chosen = selected && selected.length > 0 ? selected : ALL_STATUSES;
  const allowed = chosen.filter(
    (status) => !(status === 'PENDING' && !showPending) && !(status === 'COMPLETED' && !showCompleted),
  );
  return allowed.length === ALL_STATUSES.length ? undefined : allowed;
}

/** Builds the body of the request from the filters on the events page. Filters that are empty are left out. */
export function buildExportRequest(
  filters: EventFilterValues | null | undefined,
  showPending: boolean,
  showCompleted: boolean,
  mapDataUrl?: string,
): ExportRequest {
  const request: ExportRequest = {};

  const statuses = exportStatuses(filters?.statuses, showPending, showCompleted);
  if (statuses && statuses.length > 0) {
    request.statuses = statuses;
  }
  if (filters?.reportedByIds && filters.reportedByIds.length > 0) {
    request.reportedByIds = filters.reportedByIds;
  }
  if (filters?.possibleIdentifications && filters.possibleIdentifications.length > 0) {
    request.possibleIdentifications = filters.possibleIdentifications;
  }
  if (mapDataUrl) {
    request.mapDataUrl = mapDataUrl;
  }

  return request;
}

/** The pin color of a report: its SRE category, else its status, the same as the map on the events page. */
export function exportPinColor(report: HeroReport): string {
  return (
    sreCategoryPinColor(report.possibleIdentification) ??
    STATUS_PIN_COLORS[(report.status ?? '').toUpperCase()] ??
    OTHER_STATUS_PIN_COLOR
  );
}

/** The pins for the map in the export: one for each report, labeled with the report's id. */
export function exportPins(reports: HeroReport[]): ExportPin[] {
  return reports.map((report) => ({
    label: reportNumberLabel(report.reportNumber),
    longitude: report.longitude,
    latitude: report.latitude,
    color: exportPinColor(report),
  }));
}

/**
 * The name to save an export report as: the one the API gave it (<region>-<organization>-<date>.pdf), without any
 * folder part, or report.pdf if there is none.
 */
export function exportFileName(fileName: string | null | undefined): string {
  const name = (fileName ?? '').split(/[\\/]/).pop()?.trim() ?? '';
  return name === '' ? 'report.pdf' : name;
}

/** The error code the API sent with a failed request or a failed export, if any. */
export function apiErrorCode(error: any): string | null {
  return error?.errorCode ?? error?.error?.data?.errorCode ?? null;
}
